import {
    ADDRESS_CLAMP_TO_EDGE,
    ADDRESS_REPEAT,
    FILTER_LINEAR,
    FILTER_LINEAR_MIPMAP_LINEAR,
    PIXELFORMAT_RGBA8,
    TEXTUREPROJECTION_EQUIRECT,
    Color,
    EnvLighting,
    Texture
} from 'playcanvas';

import { Events } from './events';
import { Scene } from './scene';

type EnvPreset = {
    id: string;
    name: string;
    kind: 'none' | 'procedural' | 'image' | 'sky';
    // procedural vertical gradient (top sky -> horizon -> bottom ground)
    sky?: [number, number, number];
    horizon?: [number, number, number];
    ground?: [number, number, number];
    // representative background colour (used for the clear colour)
    bg?: [number, number, number];
    // image url for photo-based environments
    url?: string;
};

// tunable parameters for the analytic 'sky' preset (Unity default-skybox
// equivalent: sun disc + Rayleigh-ish atmosphere gradient + flat ground band)
type SkyParams = {
    sunElevation: number; // degrees above the horizon
    sunAzimuth: number; // degrees, 0 = +Z
    atmosphereThickness: number; // 0-1, higher = hazier/whiter horizon
    skyTint: [number, number, number];
    groundColor: [number, number, number];
    sunIntensity: number;
};

const defaultSkyParams = (): SkyParams => ({
    sunElevation: 35,
    sunAzimuth: 30,
    atmosphereThickness: 0.4,
    skyTint: [0.35, 0.55, 0.95],
    groundColor: [0.3, 0.28, 0.25],
    sunIntensity: 1.2
});

// built-in environment presets. procedural ones are generated at runtime (no
// downloads, work offline) and also provide image-based lighting so polygonal
// meshes — including metallic materials — are lit correctly.
const presets: EnvPreset[] = [
    { id: 'none', name: 'None', kind: 'none', bg: [0, 0, 0] },
    { id: 'studio', name: 'Studio (neutral)', kind: 'procedural', sky: [0.9, 0.9, 0.92], horizon: [0.55, 0.55, 0.58], ground: [0.2, 0.2, 0.22], bg: [0.5, 0.5, 0.53] },
    { id: 'daylight', name: 'Daylight', kind: 'procedural', sky: [0.35, 0.55, 0.95], horizon: [0.8, 0.88, 1.0], ground: [0.35, 0.3, 0.24], bg: [0.55, 0.7, 0.95] },
    { id: 'sunset', name: 'Sunset', kind: 'procedural', sky: [0.25, 0.2, 0.45], horizon: [1.0, 0.5, 0.2], ground: [0.18, 0.12, 0.1], bg: [0.85, 0.45, 0.25] },
    { id: 'overcast', name: 'Overcast', kind: 'procedural', sky: [0.7, 0.72, 0.75], horizon: [0.82, 0.83, 0.85], ground: [0.4, 0.4, 0.42], bg: [0.72, 0.74, 0.77] },
    { id: 'night', name: 'Night', kind: 'procedural', sky: [0.02, 0.03, 0.08], horizon: [0.08, 0.1, 0.18], ground: [0.02, 0.02, 0.04], bg: [0.04, 0.05, 0.1] },
    { id: 'echopark', name: 'Echo Park (photo)', kind: 'image', url: './static/env/Echopark.png' },
    { id: 'vertebrae', name: 'Vertebrae (photo)', kind: 'image', url: './static/env/VertebraeHDRI_v1_512.png' },
    { id: 'sky', name: 'Unity Sky (procedural)', kind: 'sky', bg: [0.55, 0.7, 0.95] }
];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const DEG_TO_RAD = Math.PI / 180;

const registerEnvironmentEvents = (events: Events, scene: Scene) => {
    const device = scene.graphicsDevice;
    const pcScene = scene.app.scene;

    let currentId = 'none';
    let skyParams: SkyParams = defaultSkyParams();
    let source: Texture = null;
    let atlas: Texture = null;
    let skybox: Texture = null;

    const releaseTextures = () => {
        if (atlas) {
            atlas.destroy();
            atlas = null;
        }
        if (skybox) {
            skybox.destroy();
            skybox = null;
        }
        if (source) {
            source.destroy();
            source = null;
        }
    };

    // build an equirect gradient texture for a procedural preset
    const buildGradient = (preset: EnvPreset): Texture => {
        const w = 256;
        const h = 128;
        const data = new Uint8Array(w * h * 4);
        const { sky, horizon, ground } = preset;

        for (let y = 0; y < h; ++y) {
            const v = y / (h - 1); // 0 = top (sky), 1 = bottom (ground)
            let r: number, g: number, b: number;
            if (v < 0.5) {
                const t = v / 0.5;
                r = lerp(sky[0], horizon[0], t);
                g = lerp(sky[1], horizon[1], t);
                b = lerp(sky[2], horizon[2], t);
            } else {
                const t = (v - 0.5) / 0.5;
                r = lerp(horizon[0], ground[0], t);
                g = lerp(horizon[1], ground[1], t);
                b = lerp(horizon[2], ground[2], t);
            }
            for (let x = 0; x < w; ++x) {
                const i = (y * w + x) * 4;
                data[i] = Math.round(r * 255);
                data[i + 1] = Math.round(g * 255);
                data[i + 2] = Math.round(b * 255);
                data[i + 3] = 255;
            }
        }

        const tex = new Texture(device, {
            name: `env-${preset.id}`,
            width: w,
            height: h,
            format: PIXELFORMAT_RGBA8,
            projection: TEXTUREPROJECTION_EQUIRECT,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_REPEAT,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        tex.lock().set(data);
        tex.unlock();
        return tex;
    };

    // build an analytic equirect sky (Unity default-skybox equivalent): a
    // Rayleigh-ish gradient from a tinted zenith to a hazy/whitened horizon,
    // an analytic sun disc, and a flat ground band below the horizon.
    const buildProceduralSky = (params: SkyParams): Texture => {
        const w = 256;
        const h = 128;
        const data = new Uint8Array(w * h * 4);
        const { sunElevation, sunAzimuth, atmosphereThickness, skyTint, groundColor, sunIntensity } = params;

        const sunElRad = sunElevation * DEG_TO_RAD;
        const sunAzRad = sunAzimuth * DEG_TO_RAD;
        const sunDir = [
            Math.cos(sunElRad) * Math.cos(sunAzRad),
            Math.sin(sunElRad),
            Math.cos(sunElRad) * Math.sin(sunAzRad)
        ];

        // near-white haze colour the sky whitens towards at the horizon
        const hazeColor: [number, number, number] = [
            lerp(skyTint[0], 1.0, 0.6),
            lerp(skyTint[1], 1.0, 0.6),
            lerp(skyTint[2], 1.0, 0.6)
        ];

        for (let y = 0; y < h; ++y) {
            const v = y / (h - 1);
            const elevation = (0.5 - v) * Math.PI; // +pi/2 top (up) .. -pi/2 bottom (down)
            const sinEl = Math.sin(elevation);
            const cosEl = Math.cos(elevation);

            for (let x = 0; x < w; ++x) {
                const azimuth = (x / w) * Math.PI * 2;
                const dir = [cosEl * Math.cos(azimuth), sinEl, cosEl * Math.sin(azimuth)];

                let r: number, g: number, b: number;
                if (sinEl >= 0) {
                    // sky: haze increases towards the horizon, controlled by atmosphere thickness
                    const haze = clamp01(1 - sinEl) ** lerp(3.0, 0.7, atmosphereThickness);
                    r = lerp(skyTint[0], hazeColor[0], haze);
                    g = lerp(skyTint[1], hazeColor[1], haze);
                    b = lerp(skyTint[2], hazeColor[2], haze);
                } else {
                    // below the horizon: blend from horizon haze into the flat ground colour
                    const t = clamp01(-sinEl * 6);
                    r = lerp(hazeColor[0], groundColor[0], t);
                    g = lerp(hazeColor[1], groundColor[1], t);
                    b = lerp(hazeColor[2], groundColor[2], t);
                }

                // analytic sun: tight bright disc plus a softer glow
                const cosAngle = clamp01(dir[0] * sunDir[0] + dir[1] * sunDir[1] + dir[2] * sunDir[2]);
                const disc = cosAngle ** 2000 * sunIntensity * 3;
                const glow = cosAngle ** 32 * sunIntensity * 0.35;
                r += disc + glow;
                g += (disc + glow) * 0.95;
                b += (disc + glow) * 0.85;

                const i = (y * w + x) * 4;
                data[i] = Math.min(255, Math.round(r * 255));
                data[i + 1] = Math.min(255, Math.round(g * 255));
                data[i + 2] = Math.min(255, Math.round(b * 255));
                data[i + 3] = 255;
            }
        }

        const tex = new Texture(device, {
            name: 'env-sky',
            width: w,
            height: h,
            format: PIXELFORMAT_RGBA8,
            projection: TEXTUREPROJECTION_EQUIRECT,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_REPEAT,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        tex.lock().set(data);
        tex.unlock();
        return tex;
    };

    // turn an equirect source texture into an env atlas (for lighting/reflections)
    // and a skybox cubemap (for the visible background) and apply both
    const applySource = (src: Texture) => {
        releaseTextures();
        source = src;
        const lighting = EnvLighting.generateLightingSource(src);
        atlas = EnvLighting.generateAtlas(lighting);
        lighting.destroy();
        skybox = EnvLighting.generateSkyboxCubemap(src);
        pcScene.envAtlas = atlas;
        pcScene.skybox = skybox;
        scene.forceRender = true;
    };

    const loadImage = (preset: EnvPreset) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            // only apply if this is still the active preset
            if (currentId !== preset.id) {
                return;
            }
            const tex = new Texture(device, {
                name: `env-${preset.id}`,
                projection: TEXTUREPROJECTION_EQUIRECT,
                mipmaps: true,
                minFilter: FILTER_LINEAR_MIPMAP_LINEAR,
                magFilter: FILTER_LINEAR,
                addressU: ADDRESS_REPEAT,
                addressV: ADDRESS_CLAMP_TO_EDGE
            });
            tex.setSource(img);
            applySource(tex);
        };
        img.onerror = () => {
            console.warn(`failed to load environment image '${preset.url}'`);
        };
        img.src = preset.url;
    };

    const setEnvironment = (id: string) => {
        const preset = presets.find(p => p.id === id) ?? presets[0];
        currentId = preset.id;

        // background clear colour follows the environment for a coherent look
        if (preset.bg) {
            events.fire('setBgClr', new Color(preset.bg[0], preset.bg[1], preset.bg[2], 1));
        }

        switch (preset.kind) {
            case 'none':
                releaseTextures();
                pcScene.envAtlas = null;
                pcScene.skybox = null;
                scene.forceRender = true;
                break;
            case 'procedural':
                applySource(buildGradient(preset));
                break;
            case 'image':
                loadImage(preset);
                break;
            case 'sky':
                applySource(buildProceduralSky(skyParams));
                break;
        }

        events.fire('environment.changed', preset.id);
    };

    // regenerate the sky texture with updated params (only takes effect
    // immediately if the sky preset is currently active)
    const updateSkyParams = (partial: Partial<SkyParams>) => {
        skyParams = { ...skyParams, ...partial };
        if (currentId === 'sky') {
            applySource(buildProceduralSky(skyParams));
        }
        events.fire('environment.skyParams.changed', skyParams);
    };

    // public API
    events.function('environment.presets', () => presets.map(p => ({ v: p.id, t: p.name })));
    events.function('environment.current', () => currentId);
    events.on('environment.set', (id: string) => setEnvironment(id));

    events.function('environment.skyParams', () => skyParams);
    events.on('environment.setSkyParams', (partial: Partial<SkyParams>) => updateSkyParams(partial));

    events.on('scene.clear', () => setEnvironment('none'));

    // persistence (.ssproj)
    events.function('docSerialize.environment', () => ({ id: currentId, skyParams }));
    events.function('docDeserialize.environment', (doc: any) => {
        // backwards compatible with older docs that stored a plain id string
        const id = typeof doc === 'string' ? doc : doc?.id;
        if (typeof doc === 'object' && doc?.skyParams) {
            skyParams = { ...defaultSkyParams(), ...doc.skyParams };
        }
        if (id) {
            setEnvironment(id);
        }
    });
};

export { registerEnvironmentEvents, SkyParams };
